<template>
  <section
    data-cy="seal-row-section"
    :class="{ 'seal-compact-mode': isCompactMode }"
  >
    <EquipmentRow :key="`${seal?.id}-${index}`">
      <template #left></template>
      <template #default="{ toggle }">
        <AddRemoveButtons
          :disable-add-above="disableAddAbove"
          :disable-add-below="disableAddBelow"
          :disable-remove="disableRemove"
          :hide-add-above="hideAddAbove"
          :hide-add-below="hideAddBelow"
          :hide-remove="hideRemove"
          :data-cy="`add-remove-buttons-${seal?.id}`"
          @add-above="$emit('add-above')"
          @add-below="$emit('add-below')"
          @remove="$emit('remove', seal)"
        >
          <template #middle-content>
            <EquipmentPanel
              data-cy="equipment-panel"
              class="w-full"
              :rule-names="
                index === 0
                  ? [
                      'operation_expansion_volume',
                      'pull_expansion_volume',
                      'upper_chamber_pull_expansion_volume',
                      'operating_seal_thrust_bearing_load',
                      'shut_in_seal_thrust_bearing_load',
                      'seal_size_top_casing',
                      'seal_size_bypass_tubing_top_casing',
                      'seal_size_bypass_tubing_bottom_casing',
                      'seal_size_bottom_casing',
                      'seal_shaft_limit_hs',
                      'seal_shaft_limit_std',
                      'seal_obsolete',
                      'seal_inclination_warning',
                      'seal_inclination_error'
                    ]
                  : [
                      'seal_size_top_casing',
                      'seal_size_bottom_casing',
                      'seal_size_bypass_tubing_top_casing',
                      'seal_size_bypass_tubing_bottom_casing',
                      'seal_shaft_limit_hs',
                      'seal_shaft_limit_std',
                      'seal_obsolete'
                    ]
              "
              :xref-id="seal.id"
              :stack="stack"
              @toggle="toggle"
            >
              <template #header>Seal Type</template>
              <template v-if="seal?.r_seal" #trigger>
                {{
                  computeSealLabel(seal?.r_seal, seal?.use_high_strength_shaft)
                }}
              </template>
            </EquipmentPanel>
          </template>
        </AddRemoveButtons>
      </template>
      <template #expand="{ toggle }">
        <SealSearchView
          :is-admin="false"
          data-cy="seal-search-view"
          @select="handleSealSelect(seal, $event, toggle)"
        />
      </template>
      <template #right>
        <div v-if="!isCompactMode" class="flex">
          <div class="mr-2">
            <ConverterInput
              label="Length"
              :model-value="seal?.r_seal?.length_inches"
              :base-unit="LengthUnits.Inches"
              :target-unit="getLargeLengthUnit('Length.convert.Inches')"
              :model="LargeLengthUnits.options()"
              mode="convert"
            />
          </div>
          <div
            class="field-checkbox align-items-center flex mb-0"
            data-cy="checkbox-wrapper"
          >
            <AlertWrapper
              :rule-names="['seal_shaft_limit_std']"
              :xref-id="seal.id"
              :stack="stack"
            >
              <Checkbox
                :model-value="seal?.use_high_strength_shaft || false"
                :disabled="!seal?.r_seal || !canEdit"
                :input-id="`high-strength-shaft-${seal?.id}`"
                binary
                data-cy="high-strength-shaft-checkbox"
                @update:model-value="
                  $emit('update:seals', {
                    ...seal,
                    use_high_strength_shaft: $event
                  })
                "
              />
              <label
                :for="`high-strength-shaft-${seal?.id}`"
                :class="{ disabled: !seal?.r_seal }"
              >
                Use High Strength Shaft
              </label>
            </AlertWrapper>
          </div>
        </div>
      </template>
      <template #right-raw>
        <div v-if="isCompactMode" class="col-12 seal-row-compact-second-row">
          <div class="seal-row-compact-length">
            <ConverterInput
              label="Length"
              :model-value="seal?.r_seal?.length_inches"
              :base-unit="LengthUnits.Inches"
              :target-unit="getLargeLengthUnit('Length.convert.Inches')"
              :model="LargeLengthUnits.options()"
              mode="convert"
            />
          </div>
          <div
            class="field-checkbox align-items-center flex mb-0 seal-row-compact-checkbox"
            data-cy="checkbox-wrapper"
          >
            <AlertWrapper
              :rule-names="['seal_shaft_limit_std']"
              :xref-id="seal.id"
              :stack="stack"
            >
              <Checkbox
                :model-value="seal?.use_high_strength_shaft || false"
                :disabled="!seal?.r_seal || !canEdit"
                :input-id="`high-strength-shaft-${seal?.id}`"
                binary
                data-cy="high-strength-shaft-checkbox"
                @update:model-value="
                  $emit('update:seals', {
                    ...seal,
                    use_high_strength_shaft: $event
                  })
                "
              />
              <label
                :for="`high-strength-shaft-${seal?.id}`"
                :class="{ disabled: !seal?.r_seal }"
              >
                {{ highStrengthShaftLabel }}
              </label>
            </AlertWrapper>
          </div>
        </div>
      </template>
    </EquipmentRow>
  </section>
</template>

<script setup lang="ts">
import type { PropType } from "vue";
import { provide, computed } from "vue";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { computeSealLabel } from "@/models/equipment/seals/properties";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import AlertWrapper from "@/components/AlertWrapper.vue";
import SealSearchView from "./SealSearchView.vue";
import type { XrefSeals } from "@/composables/forms/formTypes";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import {
  scenarioKey,
  sizingKey,
  scenarioFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import type { Seals } from "gql/api";
import { Sizing_Pumping_Type_Enum } from "gql/api";

const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const scenarioForm = injectStrict(scenarioFormKey, null);
const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);

// Computed for compact mode detection
const isCompactMode = computed(() => !!props.stack);

// Computed label for high strength shaft checkbox
const highStrengthShaftLabel = computed(() =>
  isCompactMode.value ? " Use HS Shaft" : " Use High Strength Shaft"
);

const props = defineProps({
  seal: {
    type: Object as PropType<XrefSeals[number]>,
    default: null
  },
  index: {
    type: Number,
    default: -1
  },
  canEdit: {
    type: Boolean,
    default: true
  },
  hideAddAbove: {
    type: Boolean,
    default: false
  },
  hideAddBelow: {
    type: Boolean,
    default: false
  },
  hideRemove: {
    type: Boolean,
    default: false
  },
  disableAddAbove: {
    type: Boolean,
    default: false
  },
  disableAddBelow: {
    type: Boolean,
    default: false
  },
  disableRemove: {
    type: Boolean,
    default: false
  },
  stack: {
    type: String as PropType<"upper" | "lower" | undefined>,
    default: undefined
  }
});

const $emit = defineEmits<{
  (e: "add-below"): void;
  (e: "add-above"): void;
  (e: "remove", seal: XrefSeals[number]): void;
  (e: "update:seals", seal: XrefSeals[number]): void;
}>();

function handleSealSelect(
  xrefSeal: XrefSeals[number],
  selectedSealEquipment: Seals,
  toggle: () => void
): void {
  toggle();
  const isHps =
    scenarioForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.Hps ||
    sizing?.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps;
  $emit("update:seals", {
    ...xrefSeal,
    id: xrefSeal?.id,
    seal_id: selectedSealEquipment.id,
    r_seal: selectedSealEquipment,
    use_high_strength_shaft:
      isHps
        ? (xrefSeal.use_high_strength_shaft ?? true)
        : xrefSeal.use_high_strength_shaft
  });
}

provide(
  "selected-equipment-id",
  computed(() => props.seal.r_seal?.id)
);
</script>

<style lang="scss" scoped>
section[data-cy="seal-row-section"] {
  margin-bottom: 1rem;
}
</style>
